import { useEffect } from 'react';

/**
 * A record set some screen loads, named by what it is rather than by URL.
 *
 * The app has no query cache: each loader owns its state and re-reads on a
 * `refresh()`. When a record changes somewhere other than the screen showing
 * it (the assistant edited it, or undid an edit), the writer names the topics
 * here and every mounted loader subscribed to one re-reads. Screens mounted
 * later read fresh anyway, so nothing is queued for them.
 */
export type RefreshTopic =
  | 'brand_identity'
  | 'products'
  | 'voice_examples'
  | 'proof_points'
  | 'calendar_events'
  | 'planned_posts'
  | 'campaigns'
  | 'content_goals'
  | 'runs'
  | 'brand_memories'
  | 'kb_readiness'
  // The account's plan (`PlanProvider`): after checkout, the billing portal or
  // a staff change. Account-wide, so any brand id of the account reaches it.
  | 'entitlements';

interface RefreshSignal {
  businessId: string;
  topics: readonly RefreshTopic[];
  /** The one record that moved, when the writer knows it; null = any of them. */
  id: string | null;
}

type RefreshListener = (signal: RefreshSignal) => void;

const listeners = new Set<RefreshListener>();

/** Asks every mounted loader of these topics, for this brand, to re-read. */
export function requestRefresh(
  businessId: string,
  topics: readonly RefreshTopic[],
  id: string | null = null,
): void {
  if (topics.length === 0) return;
  const signal: RefreshSignal = { businessId, topics, id };
  listeners.forEach((listener) => listener(signal));
}

/**
 * Re-reads when `topic` is refreshed for `businessId`. `onRefresh` receives
 * the record id the writer named (null when it named none), so a loader of one
 * record can ignore changes to another. Pass a stable callback (`useCallback`):
 * the subscription is renewed whenever it changes.
 */
export function useRefreshOn(
  businessId: string | null,
  topic: RefreshTopic | null,
  onRefresh: (id: string | null) => void,
): void {
  useEffect(() => {
    if (!businessId || !topic) return;
    const listener: RefreshListener = (signal) => {
      if (signal.businessId !== businessId || !signal.topics.includes(topic)) return;
      onRefresh(signal.id);
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, [businessId, topic, onRefresh]);
}
